import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { getDocumentById, updateDocument, type DocumentRecord } from '../services/documentsService';
import { getClientById, type ClientItem } from '../services/clientService';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';
import {
  GenericDocumentView,
  PlanDirecteurView,
  Roadmap24View,
  ObjectifsKPIView,
  CahierDeChargeView,
  MatriceRACIView,
  PlanBudgetaireView,
  RegistreAmeliorationContinueView,
  RegistreRisquesTIView,
  OffreServiceBZView,
  EbaucheOdsView,
  extractPlanDirecteurVizData,
  extractRoadmap24VizData,
} from './DocumentViewer';
import DocumentAIEditModal from './DocumentAIEditModal';
import DocumentManualEditModal from './DocumentManualEditModal';
import KmiSpiderChart from './KmiSpiderChart';
import Client2026MaturitySpiders from './Client2026MaturitySpiders';

interface ManagedDocumentViewerModalProps {
  isOpen: boolean;
  documentId: number;
  onClose: () => void;
}

export default function ManagedDocumentViewerModal({
  isOpen,
  documentId,
  onClose,
}: ManagedDocumentViewerModalProps) {
  const [document, setDocument] = useState<DocumentRecord | null>(null);
  const [client, setClient] = useState<ClientItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isManualEditOpen, setIsManualEditOpen] = useState(false);
  const [editableContent, setEditableContent] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    if (!isOpen || !documentId) return;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const doc = await getDocumentById(documentId);
        if (!doc) {
          setError('Document introuvable');
          setIsLoading(false);
          return;
        }
        setDocument(doc);
        // Initialiser le contenu éditable
        const markdown = (doc.content && (doc.content.markdown as string)) || '';
        setEditableContent(markdown);

        const c = await getClientById(doc.client_id);
        setClient(c);
      } catch (e: any) {
        console.error('Erreur chargement document:', e);
        setError(e?.message || 'Erreur lors du chargement du document');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isOpen, documentId]);

  if (!isOpen) return null;

  const markdown: string = editableContent || (document && document.content && (document.content.markdown as string)) || 'Document vide.';

  const docType = DOCUMENT_TYPES.find((t) => t.code === document?.document_type_code);
  const companyName = client?.nom || 'Client';
  const color: 'blue' | 'indigo' | 'purple' | 'orange' | 'emerald' =
    (docType?.color as any) || 'blue';

  const isPlanDirecteur =
    document?.document_type_code === 'PLAN_DIRECTEUR' ||
    document?.document_type_code === 'PLAN_DIRECTEUR_5_ANS_V2';
  const isRoadmap24 = document?.document_type_code === 'ROADMAP_24M';
  const isObjectifsKPI = document?.document_type_code === 'OBJECTIFS_KPI_TI';
  const isCahierCharge = document?.document_type_code === 'CAHIER_CHARGE';
  const isMatriceRACI = document?.document_type_code === 'MATRICE_RACI_TI';
  const isPlanBudgetaire = document?.document_type_code === 'PLAN_BUDGETAIRE_TI_N1' || document?.document_type_code === 'PLAN_BUDGETAIRE_5_ANS';
  const isRegistreAmelioration = document?.document_type_code === 'REGISTRE_AMELIORATION_CONTINUE_TI';
  const isRegistreRisques = document?.document_type_code === 'REGISTRE_RISQUES_TI';
  const isOffreServiceBZ = document?.document_type_code === 'OFFRE_SERVICE_BZ';
  const isEbaucheOds = document?.document_type_code === 'EBAUCHE_ODS';
  const isODS = document?.document_type_code === 'ODS';
  const isRapportIncident = document?.document_type_code === 'RAPPORT_INCIDENT';
  const isKmi =
    document?.document_type_code === 'PRISME_KMI' || document?.document_type_code === 'PRISME_KMI_CIBLE';
  const isClient2026Analysis = document?.document_type_code === 'PRISE_BESOIN_CLIENT_2026_ANALYSE';
  const showManualEditODS = isOffreServiceBZ || isEbaucheOds || isODS || isRapportIncident || isKmi;

  const kmiVariant: 'actuelle' | 'cible' =
    (document as any)?.metadata?.kmi?.variant === 'cible' || document?.document_type_code === 'PRISME_KMI_CIBLE'
      ? 'cible'
      : 'actuelle';

  const kmiPillars: Array<{
    title?: string;
    averageScore?: number;
    averagePercent?: number;
    countedAnswers?: number;
  }> =
    (document as any)?.metadata?.kmi?.pillars && Array.isArray((document as any).metadata.kmi.pillars)
      ? (document as any).metadata.kmi.pillars
      : [];
  const client2026MaturityProfile = (document as any)?.metadata?.client2026_maturity_profile;

  const handleContentUpdate = async (newContent: string) => {
    if (!document) return;
    
    setEditableContent(newContent);
    
    // Mettre à jour dans la base de données
    try {
      const updatedDoc = await updateDocument(document.id, {
        content: {
          ...document.content,
          markdown: newContent,
        },
      });
      
      setDocument(updatedDoc);
      setShowSuccessMessage(true);
      
      // Masquer le message après 5 secondes
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    } catch (e: any) {
      console.error('Erreur mise à jour document:', e);
      throw new Error(`Erreur lors de la sauvegarde: ${e?.message || 'Erreur inconnue'}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">
              Document généré – {docType?.code || document?.document_type_code}
            </p>
            <h2 className="text-xl font-bold">
              {document?.title || docType?.title || 'Document PRIsME'}
            </h2>
            <p className="text-[11px] text-white/80 mt-1">{companyName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
              <X className="w-5 h-5 text-white" />
            </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 text-sm text-gray-700">
          <div className="max-w-5xl mx-auto">
          {isLoading ? (
            <p className="text-xs text-gray-500">Chargement du document...</p>
          ) : error ? (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[11px] text-red-700">
              {error}
            </div>
          ) : (
            <div className="max-w-5xl mx-auto">
              {showSuccessMessage && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>Document modifié avec succès ! Les changements ont été sauvegardés.</span>
                </div>
              )}
              {isPlanDirecteur ? (
                <PlanDirecteurView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  contentOnly
                  extractedVizData={extractPlanDirecteurVizData(markdown)}
                />
              ) : isRoadmap24 ? (
                <Roadmap24View
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  contentOnly
                  extractedVizData={extractRoadmap24VizData(markdown)}
                />
              ) : isObjectifsKPI ? (
                <ObjectifsKPIView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                />
              ) : isCahierCharge ? (
                <CahierDeChargeView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                />
              ) : isMatriceRACI ? (
                <MatriceRACIView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                />
              ) : isPlanBudgetaire ? (
                <PlanBudgetaireView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  contentOnly
                  extractedVizData={null}
                />
              ) : isRegistreAmelioration ? (
                <RegistreAmeliorationContinueView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                />
              ) : isRegistreRisques ? (
                <RegistreRisquesTIView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                />
              ) : isOffreServiceBZ ? (
                <OffreServiceBZView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  onEditManually={showManualEditODS ? () => setIsManualEditOpen(true) : undefined}
                />
              ) : isEbaucheOds ? (
                <EbaucheOdsView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  onEditManually={showManualEditODS ? () => setIsManualEditOpen(true) : undefined}
                  metadata={(document as any)?.metadata}
                />
              ) : isClient2026Analysis ? (
                <GenericDocumentView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  documentTitle={document?.title || docType?.title}
                  documentTypeCode={document?.document_type_code}
                  metadata={(document as any)?.metadata}
                  color={color}
                  pdfHeader={<Client2026MaturitySpiders profile={client2026MaturityProfile} />}
                />
              ) : isKmi ? (
                <div className="space-y-6">
                  <GenericDocumentView
                    content={markdown}
                    companyName={companyName}
                    onEdit={() => setIsEditorOpen(true)}
                    onEditManually={showManualEditODS ? () => setIsManualEditOpen(true) : undefined}
                    documentTitle={document?.title || docType?.title}
                    documentTypeCode={document?.document_type_code}
                    color={color}
                    pdfHeader={
                      kmiPillars.length > 0 ? (
                        <KmiSpiderChart
                          items={kmiPillars.map((p) => {
                            const pillar =
                              String(p.title || '').replace(/^Pilier\s*\d+\s*[—-]\s*/i, '') || 'Pilier';
                            const rawScore =
                              p.averageScore != null && Number.isFinite(Number(p.averageScore))
                                ? Number(p.averageScore)
                                : p.averagePercent != null && Number.isFinite(Number(p.averagePercent))
                                  ? Number(p.averagePercent) / 20
                                  : null;
                            const hasCounted =
                              typeof p.countedAnswers === 'number'
                                ? p.countedAnswers > 0
                                : rawScore != null && rawScore > 0;
                            return { pillar, score: hasCounted ? rawScore : null };
                          })}
                          title={
                            kmiVariant === 'cible'
                              ? 'Spider KMI — maturité cible Futur'
                              : 'Spider KMI — maturité actuelle'
                          }
                          variant={kmiVariant}
                        />
                      ) : undefined
                    }
                  />
                </div>
              ) : (
                <GenericDocumentView
                  content={markdown}
                  companyName={companyName}
                  onEdit={() => setIsEditorOpen(true)}
                  onEditManually={showManualEditODS ? () => setIsManualEditOpen(true) : undefined}
                  documentTitle={document?.title || docType?.title}
                  documentTypeCode={document?.document_type_code}
                  metadata={(document as any)?.metadata}
                  color={color}
                />
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Éditeur IA avec progression */}
      <DocumentAIEditModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        documentType={document?.document_type_code || ''}
        documentTitle={document?.title || ''}
        companyName={companyName}
        currentContent={markdown}
        onContentUpdate={handleContentUpdate}
      />
      {/* Éditeur manuel (ODS / Offre de service) */}
      <DocumentManualEditModal
        isOpen={isManualEditOpen}
        onClose={() => setIsManualEditOpen(false)}
        content={markdown}
        documentTitle={document?.title || docType?.title || 'Document'}
        onSave={handleContentUpdate}
      />
    </div>
  );
}
